import {
  Dialog as DialogPrimitive,
  DialogClose as DialogClosePrimitive,
  DialogContent as DialogContentPrimitive,
  DialogTrigger as DialogTriggerPrimitive,
} from "@radix-ui/react-dialog";
import {
  CheckCircledIcon,
  Cross1Icon,
  CrossCircledIcon,
  DotsVerticalIcon,
  ExclamationTriangleIcon,
} from "@radix-ui/react-icons";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { flexRender, useReactTable } from "@tanstack/react-table";
import {
  ColumnDef,
  ColumnSort,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  Row,
  SortingState,
  VisibilityState,
} from "@tanstack/table-core";
import { AxiosError } from "axios";
import { capitalCase, kebabCase } from "change-case";
import addDays from "date-fns/addDays";
import differenceInDays from "date-fns/differenceInDays";
import format from "date-fns/format";
import formatDistanceToNow from "date-fns/formatDistanceToNow";
import isSameDay from "date-fns/isSameDay";
import subDays from "date-fns/subDays";
import {
  HeartIcon,
  MessageCircleIcon,
  Repeat2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  RocketIcon,
  Loader2Icon,
  ThumbsDown,
  ThumbsUp,
  MousePointer2Icon,
  PercentIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  CalendarIcon,
  TicketPercentIcon,
  TrashIcon,
  PlusIcon,
  Edit2,
  ListFilterIcon,
} from "lucide-react";
import {
  createContext,
  useCallback,
  use,
  useEffect,
  useMemo,
  useState,
  Fragment,
  useRef,
} from "react";
import { flushSync } from "react-dom";

import {
  AddStickPostOptions,
  AddStickyPostButton,
} from "@/components/AddStickyPostsButton";
import { Banner } from "@/components/Callout";
import { preventBubble } from "@/components/canvas-editor/utils";
import { DepreactedFeedFeaturesBanner } from "@/components/DepreactedFeedFeaturesBanner";
import { Description } from "@/components/Description";
import { FeedAccessErrorPage } from "@/components/FeedAccessErrorPage";
import { FeedEditor } from "@/components/FeedEditor";
import { FeedPulseCard } from "@/components/FeedPulseCard";
import { FeedSuggestions } from "@/components/FeedSuggestions";
import { GymInvitation } from "@/components/GymInvitation";
import { useToast } from "@/components/hooks/use-toast";
import { useDelay } from "@/components/hooks/useDelay";
import { useEvent } from "@/components/hooks/useEvent";
import { InboundCampaignTable } from "@/components/InboundCampaignTable";
import { LoadMoreSpinner } from "@/components/LoadMoreSpinner";
import { Page } from "@/components/Page";
import {
  HideSensitiveContentContext,
  PostText,
  PreviewPost,
} from "@/components/PreviewFeed";
import { SimpleEditor } from "@/components/SimpleEditor";
import { Spinner } from "@/components/Spinner";
import { StickyPostMenuItems } from "@/components/StickyPostMenuItems";
import { StickySetBadge } from "@/components/StickySetRowControls";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button, ButtonLink } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  useCarousel,
} from "@/components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input, NumberInput } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  MenuCheckboxItem,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@/components/ui/menu";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNextButton,
  PaginationPreviousButton,
} from "@/components/ui/pagination";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderSortButton,
  TableRow,
} from "@/components/ui/table";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { Blockquote, H2 } from "@/components/ui/typography";
import { UserAnalyticsSummary } from "@/components/UserAnalyticsSummary";
import { ViewFeedHeader } from "@/components/ViewFeedHeader";
import {
  campaignsForAlgorithmQueryOptions,
  algorithmStatsQueryOptions,
  feedQueryOptions,
  getStickyPostsOptions,
  getTopContentQueryOptions,
  stickyPostsPerformanceQueryOptions,
  getCurrentUserQueryOptions,
  StickyPostPerformance,
  feedEngagementQueryOptions,
  FeedEngagementResult,
  FeedEngagementSort,
  createCoupon,
  getCouponsQueryOptions,
  Coupon,
  deleteCoupon,
  getBackfillStatus,
  getFeedPulseQueryOptions,
  getFeedPulseStatusQueryOptions,
  requestFeedPulseNow,
} from "@/lib/api";
import { addSticky } from "@/lib/feedWrites";
import { Feed, StickyPost } from "@/lib/types";
import { useFeedAccess } from "@/lib/useFeedAccess";
import { atUriToBskyUrl, cn, formatBigNumber } from "@/lib/utils";

/**
 * The owner's Feed Pulse panel: the card readers see on the public page, plus
 * what is happening behind it (writing now, last attempt failed, not turned
 * on yet) and a button to write yesterday's pulse without waiting for 06:20
 * UTC. Readers only ever get the card.
 *
 * 🔑 OWNER ONLY, AND NOT BEHIND A CAPABILITY. Both endpoints this panel drives
 * (`pulse-now`, `pulse-status`) are guarded server-side by `authorize_object`,
 * i.e. plain ownership -- so a collaborator who was shown this panel could only
 * ever press buttons that 403. A pulse is also written against the OWNER's
 * co/core connection and their balance, which is the same reason Patreon is
 * owner-only: it settles against an account a collaborator does not hold.
 */
function FeedPulseSection({ feedId }: { feedId: string }) {
  const id = parseInt(feedId);
  const { data: feed } = useSuspenseQuery(feedQueryOptions(id));
  const access = useFeedAccess(feed);
  // The public endpoint 404s for private feeds on purpose; the owner endpoint
  // (pulse-status) carries the same payload for any feed the owner can see.
  const { data: publicPulse } = useQuery({
    ...getFeedPulseQueryOptions(id),
    enabled: Boolean(feed.public),
  });
  const enabled = Boolean(feed.settings?.feed_pulse_enabled);
  const { data: status } = useQuery({
    ...getFeedPulseStatusQueryOptions(id),
    // GA since 2026-09-17; this used to be gated on the feed_pulse flag too.
    // `isOwner` keeps collaborators from firing a request that can only 403.
    enabled: enabled && access.isOwner,
  });
  const pulse = status?.latest_pulse ?? publicPulse ?? null;
  const { toast } = useToast();
  const pulseNowMutation = useMutation({
    mutationFn: () => requestFeedPulseNow(id),
    onSuccess: (result) =>
      toast({
        title: result.queued ? "Writing yesterday's pulse" : "Already there",
        description: result.queued
          ? "This usually takes one to three minutes. The card updates on its own."
          : `Yesterday's pulse is already ${result.state}.`,
      }),
    onError: (error) =>
      toast({
        title: "Could not start a pulse",
        description: error.message,
        variant: "destructive",
      }),
  });


  const lastJob = status?.last_job;
  const inFlight = lastJob?.state === "queued" || lastJob?.state === "running";
  const failed = lastJob?.state === "failed";
  // A fresh pulse is dated yesterday (UTC); anything older is stale enough to offer a rewrite.
  const yesterday = new Date();
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  const yesterdayIso = yesterday.toISOString().slice(0, 10);
  const current = pulse?.pulse_date === yesterdayIso;

  // Below every hook, so hook order is identical for owners and collaborators.
  if (!access.isOwner) return null;

  return (
    <section aria-label="Feed Pulse" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <H2 className="mb-0">Feed Pulse</H2>
        {/* GA since 2026-09-17: this strip used to be beta-only. */}
        {(
          <div className="flex items-center gap-3 text-xs text-slate-dim">
            {enabled && inFlight && (
              <span>Writing yesterday&apos;s pulse now (one to three minutes)&hellip;</span>
            )}
            {enabled && !inFlight && failed && (
              <span>
                Last attempt failed
                {lastJob?.error_code ? ` (${lastJob.error_code})` : ""}.
              </span>
            )}
            {enabled && !inFlight && (
              <Button
                variant="outline"
                size="sm"
                disabled={pulseNowMutation.isPending}
                onClick={() => pulseNowMutation.mutate()}
              >
                {current ? "Write yesterday's pulse again" : "Write yesterday's pulse"}
              </Button>
            )}
            {!enabled && (
              <ButtonLink
                to="/app/feed/$feedId/settings"
                params={{ feedId }}
                variant="outline"
                size="sm"
              >
                Turn on Feed Pulse
              </ButtonLink>
            )}
          </div>
        )}
      </div>
      {pulse ? (
        <FeedPulseCard pulse={pulse} />
      ) : (
        <div className="text-sm text-slate-dim border rounded-md border-dashed border-slate-6 dark:border-slatedark-6 px-4 py-3">
          {enabled
            ? inFlight
              ? `The first pulse is being written on the co/core network. It will appear here${feed.public ? " and at the top of your public feed page" : ""}.`
              : `No pulse yet. Each morning after 06:20 UTC a short summary of yesterday's most-viewed posts appears here${feed.public ? " and on your public feed page" : ""}, or write one now.`
            : `A daily, cited summary of yesterday's most-viewed posts, written on the co/core network with your own credits. Shown here${feed.public ? " and at the top of your public feed page" : " (this feed is private, so only you see it)"}.`}
        </div>
      )}
    </section>
  );
}

export const Route = createFileRoute("/app/_sidebar-layout/feed/$feedId/view")({
  component: RouteComponent,
  // A revoked collaborator gets 403 here; say so instead of bouncing them to /login.
  errorComponent: FeedAccessErrorPage,
  loader: async ({ context: { queryClient }, params: { feedId } }) => {
    const [feed] = await Promise.all([
      queryClient.ensureQueryData(feedQueryOptions(parseInt(feedId))),
      queryClient.ensureQueryData(getCurrentUserQueryOptions()),
      queryClient.prefetchQuery(
        getTopContentQueryOptions("readers", parseInt(feedId)),
      ),
      queryClient.ensureQueryData(
        getStickyPostsOptions({ algo_id: parseInt(feedId) }),
      ),
      queryClient.ensureQueryData(
        stickyPostsPerformanceQueryOptions(parseInt(feedId)),
      ),

      queryClient.ensureQueryData(
        algorithmStatsQueryOptions({ algorithmId: parseInt(feedId) }),
      ),
    ]);

    return {
      title: feed.display_name,
    };
  },
  head: ({ loaderData }) => {
    return { meta: [{ title: loaderData?.title }] };
  },
});

function FeedAnalyticsChart({ feedId }: { feedId: string }) {
  const feedNumber = parseInt(feedId);
  const { data: analytics } = useSuspenseQuery(
    algorithmStatsQueryOptions({ algorithmId: feedNumber }),
  );

  return (
    <UserAnalyticsSummary
      variant="compact"
      analytics={analytics.details}
      summary={analytics.summary}
      query={({ days }) =>
        algorithmStatsQueryOptions({ days, algorithmId: feedNumber })
      }
    />
  );
}

const FeedIdContext = createContext<string | undefined>(undefined);

/**
 * Whether this row belongs to a sticky set, or just to this feed.
 *
 * Worth a column of its own: a set-managed row looks identical to a local one,
 * and editing it here would be silently overwritten the next time the set is
 * applied.
 */
function StickySetCell({ row }: { row: Row<StickyPost> }) {
  const feedId = use(FeedIdContext);

  if (!feedId) {
    return null;
  }

  return (
    <div className="max-w-[9rem]">
      <StickySetBadge
        feedId={parseInt(feedId)}
        stickyPostId={row.original.id}
        stickyType={row.original.sticky_type}
      />
    </div>
  );
}

function StickyPostActions({
  row,
}: {
  row: Row<StickyPost & { performance?: StickyPostPerformance }>;
}) {
  const feedId = use(FeedIdContext);

  if (!feedId) {
    return null;
  }

  return (
    <div className="flex justify-end pr-2" {...preventBubble}>
      <DropdownMenu>
        <MenuTrigger asChild>
          <Button variant="ghost" size="smIcon">
            <span className="sr-only">Pinned post actions</span>
            <DotsVerticalIcon />
          </Button>
        </MenuTrigger>
        <MenuContent align="end">
          <StickyPostMenuItems
            feedId={parseInt(feedId)}
            stickyPostId={row.original.id}
            assocId={row.original.assoc_id}
            isActive={row.original.is_active}
            postUri={row.original.post.uri}
            stickyType={row.original.sticky_type}
          />
        </MenuContent>
      </DropdownMenu>
    </div>
  );
}

const stickyPostColumns: ColumnDef<
  StickyPost & { performance?: StickyPostPerformance }
>[] = [
  {
    accessorKey: "is_active",
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="is_active"
        sorting={table.getState().sorting}
      >
        Active
      </TableHeaderSortButton>
    ),
    size: 100,
    cell: ({ row }) =>
      row.original.is_active ? (
        <Badge variant="success">
          <CheckCircledIcon /> Active
        </Badge>
      ) : (
        <Badge>Inactive</Badge>
      ),
  },
  {
    id: "sticky_set",
    header: "Source",
    size: 140,
    cell: ({ row }) => <StickySetCell row={row} />,
  },
  {
    accessorKey: "sticky_type",
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="sticky_type"
        sorting={table.getState().sorting}
      >
        Type
      </TableHeaderSortButton>
    ),
    size: 100,
    cell: ({ row }) => (
      <span className="text-sm text-slate-dim">
        {row.original.sticky_type === "pinned" ? "Pinned" : "Rotating"}
      </span>
    ),
  },
  {
    accessorKey: "order",
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="order"
        sorting={table.getState().sorting}
      >
        Order
      </TableHeaderSortButton>
    ),
    size: 32,
    cell: ({ row }) => (
      <span className="text-sm text-slate-dim">
        {row.original.order || "---"}
      </span>
    ),
  },
  {
    accessorKey: "created_at",
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="created_at"
        sorting={table.getState().sorting}
      >
        Created
      </TableHeaderSortButton>
    ),
    size: 80,
    cell: ({ row }) => (
      <span className="text-xs text-slate-dim">
        {row.original.created_at === "04/04"
          ? "Unknown"
          : format(new Date(row.original.created_at), "MM/dd")}
      </span>
    ),
  },
  {
    accessorKey: "stopping_criteria.expiration_time",
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="stopping_criteria.expiration_time"
        sorting={table.getState().sorting}
      >
        Expires
      </TableHeaderSortButton>
    ),
    size: 80,
    cell: ({ row }) => (
      <span className="text-xs text-slate-dim">
        {row.original.stopping_criteria?.expiration_time
          ? row.original.stopping_criteria.expiration_time <
            new Date().getTime() / 1000
            ? "Expired"
            : format(
                new Date(row.original.stopping_criteria.expiration_time * 1000),
                "MM/dd",
              )
          : "Never"}
      </span>
    ),
  },
  {
    accessorKey: "post",
    header: "Post",
    size: 100,
    cell: ({ row }) => (
      <div className="flex gap-4 items-center overflow-hidden">
        <div className="flex flex-col gap-1">
          <div className="text-xs text-slate-dim">
            @{row.original.post.author.handle}
          </div>
          <div className="line-clamp-2">{row.original.post.record.text}</div>
        </div>
      </div>
    ),
  },
  {
    accessorKey: "performance.all_time_total_views",
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="performance_all_time_total_views"
        sorting={table.getState().sorting}
      >
        Views
      </TableHeaderSortButton>
    ),
    size: 100,
    cell: ({ row }) => {
      return formatBigNumber(
        row.original.performance?.all_time_total_views || 0,
      );
    },
  },
  {
    accessorKey: "performance.all_time_total_users",
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="performance_all_time_total_users"
        sorting={table.getState().sorting}
      >
        Users
      </TableHeaderSortButton>
    ),
    size: 100,
    cell: ({ row }) =>
      formatBigNumber(
        row.original.performance?.all_time_unique_user_views || 0,
      ),
  },
  {
    accessorKey: "performance.today_total_views",
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="performance_today_total_views"
        sorting={table.getState().sorting}
      >
        Today's Views
      </TableHeaderSortButton>
    ),
    size: 100,
    cell: ({ row }) =>
      formatBigNumber(row.original.performance?.today_total_views || 0),
  },
  {
    accessorKey: "performance.today_unique_user_views",
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="performance_today_unique_user_views"
        sorting={table.getState().sorting}
      >
        Today's Users
      </TableHeaderSortButton>
    ),
    size: 100,
    cell: ({ row }) =>
      formatBigNumber(row.original.performance?.today_unique_user_views || 0),
  },
  {
    accessorKey: "actions",
    header: () => <div className="sr-only">Actions</div>,
    size: 100,
    cell: ({ row }) => <StickyPostActions row={row} />,
  },
];

function StickyPostTable({
  data,
  feedId,
}: {
  data: StickyPost[];
  feedId: string;
}) {
  "use no memo";

  const navigate = useNavigate({ from: "/app/feed/$feedId/view" });
  const router = useRouter();
  const { data: stickyPostPerformance } = useSuspenseQuery(
    stickyPostsPerformanceQueryOptions(parseInt(feedId)),
  );
  const withPerformance = useMemo(() => {
    return data.map((post) => ({
      ...post,
      performance: stickyPostPerformance.find(
        (p) => p.post_uri === post.post.uri,
      ),
    }));
  }, [data, stickyPostPerformance]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageSize: 10,
    pageIndex: 0,
  });
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: withPerformance,
    columns: stickyPostColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    defaultColumn: {},
    onSortingChange: setSorting,
    state: { sorting, pagination },
  });

  return (
    <FeedIdContext value={feedId}>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className="whitespace-pre"
                  style={{ width: header.getSize() }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => {
            return (
              <TableRow
                key={row.original.id}
                data-state={row.getIsSelected() && "selected"}
                className="group cursor-pointer"
                onClick={() => {
                  navigate({
                    to: "/app/feed/$feedId/post-analytics",
                    params: { feedId },
                    search: { uri: row.original.post.uri },
                  }).catch(console.error);
                }}
                onMouseEnter={() => {
                  router
                    .preloadRoute({
                      to: "/app/feed/$feedId/post-analytics",
                      params: { feedId },
                      search: { uri: row.original.post.uri },
                    })
                    .catch(console.error);
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {(table.getCanPreviousPage() || table.getCanNextPage()) && (
        <Pagination className="w-full p-2 border-t border-slate-6 dark:border-slatedark-6">
          <PaginationContent className="w-full justify-between">
            <PaginationItem>
              <PaginationPreviousButton
                disabled={!table.getCanPreviousPage()}
                onClick={() =>
                  setPagination({
                    ...pagination,
                    pageIndex: pagination.pageIndex - 1,
                  })
                }
              />
            </PaginationItem>
            <PaginationItem>
              <PaginationNextButton
                disabled={!table.getCanNextPage()}
                onClick={() =>
                  setPagination({
                    ...pagination,
                    pageIndex: pagination.pageIndex + 1,
                  })
                }
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </FeedIdContext>
  );
}

const engagementColumns: ColumnDef<FeedEngagementResult>[] = [
  {
    accessorKey: "post",
    header: "Post",
    size: 300,
    enableHiding: false,
    cell: ({ row }) => (
      <div className="flex gap-4 items-center overflow-hidden">
        <div className="flex flex-col gap-1">
          <div className="text-xs text-slate-dim">
            @{row.original.post.author.handle}
          </div>
          <div className="line-clamp-2">{row.original.post.record.text}</div>
        </div>
      </div>
    ),
  },
  {
    accessorKey: "interaction_engagement",
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="interaction_engagement"
        sorting={table.getState().sorting}
      >
        Interaction Engagement
      </TableHeaderSortButton>
    ),
    size: 80,
    cell: ({ row }) => {
      return formatBigNumber(row.original?.interaction_engagement || 0);
    },
  },
  {
    accessorKey: "interaction_like_count",
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="interaction_like_count"
        sorting={table.getState().sorting}
      >
        Interaction Likes
      </TableHeaderSortButton>
    ),
    size: 80,
    cell: ({ row }) => {
      return formatBigNumber(row.original?.interaction_like_count || 0);
    },
  },
  {
    accessorKey: "interaction_reply_count",
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="interaction_reply_count"
        sorting={table.getState().sorting}
      >
        Interaction Replies
      </TableHeaderSortButton>
    ),
    size: 80,
    cell: ({ row }) => {
      return formatBigNumber(row.original?.interaction_reply_count || 0);
    },
  },
  {
    accessorKey: "interaction_repost_count",
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="interaction_repost_count"
        sorting={table.getState().sorting}
      >
        Interaction Reposts
      </TableHeaderSortButton>
    ),
    size: 80,
    cell: ({ row }) => {
      return formatBigNumber(row.original?.interaction_repost_count || 0);
    },
  },
  {
    accessorKey: "interaction_quote_count",
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="interaction_quote_count"
        sorting={table.getState().sorting}
      >
        Interaction Quote Posts
      </TableHeaderSortButton>
    ),
    size: 80,
    cell: ({ row }) => {
      return formatBigNumber(row.original?.interaction_quote_count || 0);
    },
  },
  {
    accessorKey: "reader_engagement",
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="reader_engagement"
        sorting={table.getState().sorting}
      >
        Reader Engagement
      </TableHeaderSortButton>
    ),
    size: 80,
    cell: ({ row }) => {
      return formatBigNumber(row.original?.engagement_rate || 0);
    },
  },
  {
    accessorKey: "reader_like_count",
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="reader_like_count"
        sorting={table.getState().sorting}
      >
        Reader Likes
      </TableHeaderSortButton>
    ),
    size: 80,
    cell: ({ row }) => {
      return formatBigNumber(row.original?.reader_like_count || 0);
    },
  },
  {
    accessorKey: "reader_reply_count",
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="reader_reply_count"
        sorting={table.getState().sorting}
      >
        Reader Replies
      </TableHeaderSortButton>
    ),
    size: 80,
    cell: ({ row }) => {
      return formatBigNumber(row.original?.reader_reply_count || 0);
    },
  },
  {
    accessorKey: "reader_repost_count",
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="reader_repost_count"
        sorting={table.getState().sorting}
      >
        Reader Reposts
      </TableHeaderSortButton>
    ),
    size: 80,
    cell: ({ row }) => {
      return formatBigNumber(row.original?.reader_repost_count || 0);
    },
  },
  {
    accessorKey: "request_less",
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="request_less"
        sorting={table.getState().sorting}
      >
        Request Less
      </TableHeaderSortButton>
    ),
    size: 80,
    cell: ({ row }) => {
      return formatBigNumber(row.original?.request_less_count || 0);
    },
  },
  {
    accessorKey: "request_more",
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="request_more"
        sorting={table.getState().sorting}
      >
        Request More
      </TableHeaderSortButton>
    ),
    size: 80,
    cell: ({ row }) => {
      return formatBigNumber(row.original?.request_more_count || 0);
    },
  },
];

function DetailedViewHeader({
  id,
  children,
  sorting,
  setSorting,
}: {
  id: string;
  children: React.ReactNode;
  sorting: ColumnSort | undefined;
  setSorting: (sorting: ColumnSort | undefined) => void;
}) {
  return (
    <div className="sticky top-0 z-1000 bg-slate-app p-4 font-medium text-sm border-b border-slate-6 dark:border-slatedark-6">
      <button
        type="button"
        className="bg-slate-ghost rounded-sm p-1 flex gap-2 items-center"
        onClick={() => {
          setSorting({ id, desc: !sorting?.desc });
        }}
      >
        {children}
        {sorting?.id === id ? (
          sorting.desc ? (
            <ArrowDownIcon className="h-4 w-4" />
          ) : (
            <ArrowUpIcon className="h-4 w-4" />
          )
        ) : null}
      </button>
    </div>
  );
}

function DetailedViewStat({
  icon: Icon,
  value,
  total,
}: {
  icon: React.ElementType;
  value: number;
  total?: number;
}) {
  return (
    <div className="text-lg flex gap-1 items-center p-4 border-b border-slate-6 dark:border-slatedark-6">
      <Icon className="h-5 w-5" />
      <div className="shrink-0">
        <span className="text-slate-normal">{value}</span>
        {typeof total === "number" && ` / ${total}`}
      </div>
    </div>
  );
}

function EngagementTableDetailedView({ feedId }: { feedId: string }) {
  const [sorting, setSorting] = useState<ColumnSort | undefined>({
    id: "reader_engagement",
    desc: true,
  });
  const {
    data: engagementPages,
    fetchNextPage,
    isLoading,
  } = useInfiniteQuery(
    feedEngagementQueryOptions(parseInt(feedId), {
      limit: 10,
      sort_by: sorting?.id as FeedEngagementSort | undefined,
      order: sorting?.desc ? "desc" : "asc",
    }),
  );
  const engagement = engagementPages?.pages.flat() || [];

  return (
    <div className="fixed inset-0 bg-slate-app z-10000 max-h-screen flex flex-col">
      <div className="flex items-center gap-2 py-2 px-4 border-b border-slate-6 dark:border-slatedark-6 justify-between">
        <div className="text-lg font-medium">Engagement</div>
        <DialogClosePrimitive asChild>
          <Button variant="ghost" size="smIcon">
            <span className="sr-only">Close</span>
            <Cross1Icon />
          </Button>
        </DialogClosePrimitive>
      </div>
      <div
        className="grid min-h-0 overflow-auto w-screen"
        style={{
          gridTemplateColumns:
            "minmax(320px, 420px) 1fr 1fr 1fr 1fr 1fr 1fr 1fr",
        }}
      >
        <DetailedViewHeader id="post" sorting={sorting} setSorting={setSorting}>
          Post
        </DetailedViewHeader>
        <DetailedViewHeader
          id="global_reply_count"
          sorting={sorting}
          setSorting={setSorting}
        >
          Replies
        </DetailedViewHeader>
        <DetailedViewHeader
          id="global_repost_count"
          sorting={sorting}
          setSorting={setSorting}
        >
          Reposts
        </DetailedViewHeader>
        <DetailedViewHeader
          id="global_like_count"
          sorting={sorting}
          setSorting={setSorting}
        >
          Likes
        </DetailedViewHeader>
        <DetailedViewHeader
          id="request_less"
          sorting={sorting}
          setSorting={setSorting}
        >
          Request Less
        </DetailedViewHeader>
        <DetailedViewHeader
          id="request_more"
          sorting={sorting}
          setSorting={setSorting}
        >
          Request More
        </DetailedViewHeader>
        <DetailedViewHeader
          id="engagement_rate"
          sorting={sorting}
          setSorting={setSorting}
        >
          Engagement Rate
        </DetailedViewHeader>
        <DetailedViewHeader
          id="total_interactions"
          sorting={sorting}
          setSorting={setSorting}
        >
          Total Interactions
        </DetailedViewHeader>
        {engagement.map((row) => {
          return (
            <Fragment key={row.post.id}>
              <div className="p-4 border-b border-slate-6 dark:border-slatedark-6">
                <PreviewPost record={row.post} hideEngagement />
              </div>
              <DetailedViewStat
                icon={MessageCircleIcon}
                value={row.interaction_reply_count}
                total={row.reply_count}
              />
              <DetailedViewStat
                icon={Repeat2Icon}
                value={row.interaction_repost_count}
                total={row.repost_count}
              />
              <DetailedViewStat
                icon={HeartIcon}
                value={row.interaction_like_count}
                total={row.like_count}
              />
              <DetailedViewStat
                icon={ThumbsDown}
                value={row.request_less_count}
              />
              <DetailedViewStat
                icon={ThumbsUp}
                value={row.request_more_count}
              />
              <DetailedViewStat
                icon={PercentIcon}
                value={row.engagement_rate}
              />
              <DetailedViewStat
                icon={MousePointer2Icon}
                value={row.total_interactions}
              />
            </Fragment>
          );
        })}
        <div style={{ gridColumn: "1 / -1" }}>
          <LoadMoreSpinner
            fetchNextPage={fetchNextPage}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
}

const emptyArray: FeedEngagementResult[] = [];

function EngagementTable({ feedId }: { feedId: string }) {
  "use no memo";

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "reader_engagement",
      desc: true,
    },
  ]);

  const { data: engagementPages, isLoading } = useInfiniteQuery(
    feedEngagementQueryOptions(parseInt(feedId), {
      limit: 10,
      sort_by: sorting[0]?.id as FeedEngagementSort | undefined,
      order: sorting[0]?.desc ? "desc" : "asc",
    }),
  );
  const engagement = useMemo(
    () => engagementPages?.pages.flat().slice(0, 4),
    [engagementPages],
  );
  const lastEngagement = useRef(engagement);

  if (
    lastEngagement.current !== engagement &&
    engagement &&
    engagement.length > 0
  ) {
    lastEngagement.current = engagement;
  }

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: engagement || lastEngagement.current || emptyArray,
    columns: engagementColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    defaultColumn: {},
    onSortingChange: setSorting,
    state: { sorting },
  });

  useEffect(() => {
    const visibleColumns = localStorage.getItem(`engagement-columns-${feedId}`);

    if (visibleColumns) {
      table.setState((state) => ({
        ...state,
        columnVisibility: JSON.parse(visibleColumns) as VisibilityState,
      }));
    }
  }, [feedId, table]);

  return (
    <FeedIdContext value={feedId}>
      <div
        className={cn(
          "overflow-hidden rounded-lg",
          "bg-slate-subtle border border-slate-6 dark:border-slatedark-6",
        )}
      >
        <div
          className={cn(
            "flex items-center justify-between gap-4",
            "pl-4 pr-2 py-2 h-12",
            "border-b border-slate-6 dark:border-slatedark-6",
          )}
        >
          <div className={cn("font-medium flex items-center gap-3")}>
            Engagement
          </div>

          <div className="flex items-center justify-center gap-2">
            {isLoading && <Spinner />}
            <DropdownMenu>
              <MenuTrigger asChild>
                <Button variant="ghost" size="smIcon">
                  <span className="sr-only">Choose columns</span>
                  <ListFilterIcon />
                </Button>
              </MenuTrigger>
              <MenuContent align="end">
                {table.getAllColumns().map((c) => {
                  const accessorKey = (c.columnDef as { accessorKey?: string })
                    .accessorKey;

                  if (!accessorKey || !c.getCanHide()) {
                    return null;
                  }

                  return (
                    <MenuCheckboxItem
                      key={c.id}
                      checked={c.getIsVisible()}
                      onCheckedChange={() => {
                        // eslint-disable-next-line @eslint-react/dom/no-flush-sync
                        flushSync(() => {
                          c.toggleVisibility();
                        });
                        localStorage.setItem(
                          `engagement-columns-${feedId}`,
                          JSON.stringify(table.getState().columnVisibility),
                        );
                      }}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {capitalCase(accessorKey)}
                    </MenuCheckboxItem>
                  );
                })}
              </MenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className={cn(isLoading && "opacity-50")}>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className="whitespace-pre"
                      style={{ width: header.getSize() }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => {
                return (
                  <TableRow
                    key={row.original.post.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <div className="border-t border-slate-6 dark:border-slatedark-6 flex">
          <DialogPrimitive>
            <DialogTriggerPrimitive asChild>
              <button
                type="button"
                className="bg-slate-action rounded-sm m-0.5 p-1 flex-1 text-slate-dim text-sm"
              >
                Detailed View
              </button>
            </DialogTriggerPrimitive>
            <DialogContentPrimitive>
              <EngagementTableDetailedView feedId={feedId} />
            </DialogContentPrimitive>
          </DialogPrimitive>
        </div>
      </div>
    </FeedIdContext>
  );
}

function StickyPosts({ feed }: { feed: Feed }) {
  const { feedId } = Route.useParams();
  const feedNumber = parseInt(feedId);
  const { data, isLoading } = useQuery(
    getStickyPostsOptions({ algo_id: feedNumber, page_size: 10 }),
  );
  // The feed's own pinning card, used by whoever is allowed to pin -- rather than a second
  // pinning box elsewhere on the page for collaborators.
  const access = useFeedAccess(feed);
  const canPin = access.can("manage_sticky_posts");

  const onAddStickyPost = useCallback(
    async (options: AddStickPostOptions) => {
      // The two endpoints answer differently and the dialog reads neither; it only waits.
      await addSticky({
        feedId: feedNumber,
        options,
        asCollaborator: access.isCollaborator,
      });
    },
    [feedNumber, access.isCollaborator],
  );

  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg",
        "bg-slate-subtle border border-slate-6 dark:border-slatedark-6",
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between gap-4",
          "pl-4 pr-2 py-2 h-12",
          "border-b border-slate-6 dark:border-slatedark-6",
        )}
      >
        <div className={cn("font-medium flex items-center gap-3")}>
          Sticky Posts
        </div>
        <div className="flex gap-1">
          {canPin && (
            <AddStickyPostButton
              onAddStickyPost={onAddStickyPost}
              pinnedOnly={access.isCollaborator}
            />
          )}
        </div>
      </div>
      <div>
        {data && data.length > 0 ? (
          <>
            <StickyPostTable data={data} feedId={feedId} />
          </>
        ) : isLoading ? (
          <div className="flex items-center justify-center p-4">
            <Spinner delay={1000} />
          </div>
        ) : (
          <div className="text-slate-dim text-sm p-4">
            No sticky posts configured!
          </div>
        )}
      </div>
    </div>
  );
}

function TopContentDateButtons({
  onPrev,
  onNext,
  onHoverPrev,
  onHoverNext,
  hasNext,
}: {
  onPrev: () => void;
  onNext: () => void;
  onHoverPrev: () => void;
  onHoverNext: () => void;
  hasNext: boolean;
}) {
  return (
    <div className="flex items-center gap-1">
      <SimpleTooltip message="Previous day">
        <Button
          size="smIcon"
          variant="ghost"
          // disabled={!canScrollPrev}
          onPress={onPrev}
          onMouseEnter={onHoverPrev}
        >
          <span className="sr-only">Previous day</span>
          <ChevronLeftIcon className="h-4 w-4" />
        </Button>
      </SimpleTooltip>
      <SimpleTooltip message="Next day">
        <Button
          size="smIcon"
          variant="ghost"
          disabled={!hasNext}
          onPress={onNext}
          onMouseEnter={onHoverNext}
        >
          <span className="sr-only">Next day</span>
          <ChevronRightIcon className="h-4 w-4" />
        </Button>
      </SimpleTooltip>
    </div>
  );
}

function CarrouselDots({
  active,
  setActive,
}: {
  active: number;
  setActive: (index: number) => void;
}) {
  const { api } = useCarousel();

  return (
    <div className="flex items-center justify-center py-2">
      {Array.from({ length: api?.slideNodes().length || 0 }).map((_, i) => (
        <button
          type="button"
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={`carousel-dot-${i}`}
          aria-label={`Go to slide ${i + 1}`}
          aria-current={i === active}
          className={cn(
            "h-2 w-2 m-1 rounded-full",
            i === active ? "bg-slate-solid" : "bg-slate-action",
          )}
          onClick={() => {
            api?.scrollTo(i);
            setActive(i);
          }}
        />
      ))}
    </div>
  );
}

function getNewDate(day: Date, offset: number) {
  const currentDay = day || new Date();
  const newDay =
    offset < 0
      ? subDays(new Date(currentDay), Math.abs(offset))
      : addDays(new Date(currentDay), offset);

  return newDay;
}

function TopContent() {
  const { feedId } = Route.useParams();
  const [day, setDay] = useState<Date | undefined>();
  const [active, setActive] = useState(0);
  const { data: topContent, isPending } = useQuery(
    getTopContentQueryOptions(
      "readers",
      parseInt(feedId),
      day ? format(day, "yyyy-MM-dd") : undefined,
    ),
  );
  const fetchDay = useCallback(
    (offset: number) => {
      const currentDay = day || new Date();
      setDay(getNewDate(currentDay, offset));
    },
    [day],
  );
  const queryClient = useQueryClient();
  const ensureDay = useCallback(
    (offset: number) => {
      const currentDay = day || new Date();
      void queryClient.prefetchQuery(
        getTopContentQueryOptions(
          "readers",
          parseInt(feedId),
          format(getNewDate(currentDay, offset), "yyyy-MM-dd"),
        ),
      );
    },
    [day, feedId, queryClient],
  );

  return (
    <Carousel
      className={cn(
        "flex-1 min-w-0 flex flex-col",
        "rounded-lg px-4 pb-2",
        "bg-slate-subtle border border-slate-6 dark:border-slatedark-6",
      )}
    >
      <div className={cn("flex gap-1 sm:gap-2 shrink-0")}>
        <span
          className={cn(
            "font-semibold text-slate-dim text-xs sm:text-base flex-1 pt-4 pb-2",
          )}
        >
          Top Posts {day ? `on ${format(day, "MMM d")}` : ""}
        </span>

        <TopContentDateButtons
          onPrev={() => fetchDay(-1)}
          onHoverPrev={() => ensureDay(-1)}
          onNext={() => fetchDay(1)}
          onHoverNext={() => ensureDay(1)}
          hasNext={!isSameDay(day || new Date(), new Date())}
        />
      </div>

      <div className="w-full flex-1">
        {topContent && topContent.length > 0 ? (
          <>
            <CarouselContent className="-ml-2">
              {topContent.map((post) => (
                <CarouselItem
                  key={`${post.post.id}-${post.post.cid}`}
                  className="p-2"
                >
                  <div
                    className={cn(
                      "shrink-0 flex items-center justify-between gap-4",
                      "flex flex-col items-start",
                    )}
                  >
                    <div className="flex gap-3 items-start justify-between self-stretch">
                      <div className="flex gap-3 items-center">
                        <Avatar
                          src={post.post.author.avatar}
                          alt={post.post.author.handle}
                        />
                        <div>
                          <div className="text-sm">
                            {post.post.author.displayName}
                          </div>
                          <div className="text-xs text-slate-dim">
                            @{post.post.author.handle}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 items-center">
                        <div className="text-xs text-slate-dim">
                          {post.post.record.createdAt &&
                            formatDistanceToNow(
                              new Date(post.post.record.createdAt),
                            )}
                        </div>
                      </div>
                    </div>
                    <PostText className="line-clamp-2" record={post.post} />
                    <div className="flex items-center justify-between self-stretch">
                      <SimpleTooltip message="Interactions by readers from your feed">
                        <div className="flex gap-4 items-center text-slate-dim text">
                          <div className="flex gap-1 items-center">
                            <MessageCircleIcon className="h-3.5 w-3.5" />
                            <div>
                              <span className="text-slate-normal">
                                {post.interaction_reply_count}
                              </span>
                              {` / ${
                                post.post.replyCount ||
                                // @ts-expect-error types wrong
                                post.post.reply_count ||
                                0
                              }`}
                            </div>
                          </div>
                          <div className="flex gap-1 items-center">
                            <Repeat2Icon className="h-3.5 w-3.5" />
                            <div>
                              <span className="text-slate-normal">
                                {post.interaction_repost_count}
                              </span>
                              {` / ${
                                post.post.repostCount || post.repost_count || 0
                              }`}
                            </div>
                          </div>
                          <div className="flex gap-1 items-center">
                            <HeartIcon className="h-3.5 w-3.5" />
                            <div>
                              <span className="text-slate-normal">
                                {post.interaction_like_count}
                              </span>
                              {` / ${
                                post.post.likeCount || post.like_count || 0
                              }`}
                            </div>
                          </div>
                        </div>
                      </SimpleTooltip>

                      <Button
                        size="smIcon"
                        variant="ghost"
                        onPress={() => {
                          window.open(atUriToBskyUrl(post.post.uri), "_blank");
                        }}
                      >
                        <ExternalLinkIcon />
                        <span className="sr-only">View post</span>
                      </Button>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarrouselDots active={active} setActive={setActive} />
          </>
        ) : (
          <div className="h-full w-full flex items-center justify-center pb-6">
            {isPending ? (
              <Spinner />
            ) : (
              <div className="text-slate-dim text-sm">No data available</div>
            )}
          </div>
        )}
      </div>
    </Carousel>
  );
}

function CreateCouponDialog({
  feedId,
  children,
}: {
  feedId: string;
  children: React.ReactNode;
}) {
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [open, setOpen] = useState(false);

  const [percentage, setPercentage] = useState(1);
  const [usageLimit, setUsageLimit] = useState<number>();
  const [expirationDate, setExpirationDate] = useState<Date>();
  const [customCode, setCustomCode] = useState("");
  const [name, setName] = useState("");
  const visibleCode = customCode || kebabCase(name);
  const resetState = useEvent(() => {
    setPercentage(1);
    setUsageLimit(undefined);
    setExpirationDate(undefined);
    setCustomCode("");
    setName("");
  });
  const onOpenChange = useEvent((open: boolean) => {
    if (!open) {
      resetState();
    }
    setOpen(open);
  });

  const { toast } = useToast();
  const createCouponMutation = useMutation({
    mutationFn: async () => {
      await createCoupon({
        algorithm_id: parseInt(feedId),
        custom_code: visibleCode,
        name,
        discount_percentage: percentage * 100,
        max_uses: usageLimit,
        expires_in_days: expirationDate
          ? differenceInDays(expirationDate, new Date())
          : undefined,
      });
    },
    onSuccess: async () => {
      await navigator.clipboard.writeText(visibleCode);
      toast({
        title: "Coupon created",
        description: "The coupon has been created and copied to clipboard.",
      });
      setOpen(false);
      resetState();
    },
    onError: (error: AxiosError) => {
      toast({
        title: "Error creating coupon",
        description: (error.response?.data as { detail: string }).detail,
        variant: "destructive",
        duration: 5000,
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-md" asChild>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            createCouponMutation.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Create Discount Coupon</DialogTitle>
            <DialogDescription>
              Create a discount coupon to give to your readers.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <div className="flex flex-col gap-3">
              <Label>Name</Label>
              <Input
                value={name}
                onValueChange={setName}
                placeholder="My Discount"
                required
              />
            </div>
            <div className="flex flex-col gap-3">
              <Label>Custom Code (Optional)</Label>
              <Input
                value={visibleCode}
                onValueChange={setCustomCode}
                placeholder="my-discount"
                required
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label>Discount Percentage</Label>
              <NumberInput
                value={percentage}
                onValueChange={setPercentage}
                min={0.01}
                max={1}
                step={0.01}
                formatOptions={{ style: "percent" }}
                isRequired
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label>
                Usage Limit{" "}
                <span className="text-slate-dim text-xs">(Optional)</span>
              </Label>
              <NumberInput
                min={1}
                value={usageLimit}
                onValueChange={setUsageLimit}
                placeholder="No limit"
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label>
                Expiration Date{" "}
                <span className="text-slate-dim text-xs">(Optional)</span>
              </Label>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="justify-start bg-slate-2 dark:bg-slatedark-2 hover:bg-slate-2 dark:hover:bg-slatedark-2 border-slate-8 dark:border-slatedark-8"
                  >
                    <CalendarIcon />
                    {expirationDate ? (
                      format(expirationDate, "PPP")
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0">
                  <Calendar
                    mode="single"
                    fromDate={new Date()}
                    selected={expirationDate}
                    onSelect={(newDate) => {
                      setExpirationDate(newDate);
                      setDatePickerOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <DialogFooter className="flex justify-end gap-2">
            <DialogClosePrimitive asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClosePrimitive>
            <Button type="submit" isLoading={createCouponMutation.isPending}>
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteCouponDialog({
  coupon,
  children,
  algorithmId,
}: {
  coupon: Coupon;
  children: React.ReactNode;
  algorithmId: number;
}) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const deleteCouponMutation = useMutation({
    mutationFn: () => deleteCoupon(coupon.id, algorithmId),
    onSuccess: () => {
      toast({
        title: "Coupon deleted",
        description: "The coupon has been deleted.",
      });
      setOpen(false);
    },
    onError: (error: AxiosError) => {
      toast({
        title: "Error deleting coupon",
        description: (error.response?.data as { detail: string }).detail,
        variant: "destructive",
      });
      setOpen(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Coupon</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this coupon?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClosePrimitive asChild>
            <Button variant="ghost">Cancel</Button>
          </DialogClosePrimitive>
          <Button
            variant="destructive"
            isLoading={deleteCouponMutation.isPending}
            onClick={() => {
              deleteCouponMutation.mutate();
            }}
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageCouponsDialog({
  feedId,
  children,
}: {
  feedId: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { data: coupons } = useQuery(getCouponsQueryOptions(parseInt(feedId)));
  const { toast } = useToast();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Manage Coupons</DialogTitle>
          <DialogDescription>
            Manage the coupons for your feed.
          </DialogDescription>
        </DialogHeader>
        <div
          className="flex flex-col gap-4 py-2 focus:outline-none"
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
        >
          {coupons?.map((coupon) => (
            <div
              key={coupon.id}
              className="flex gap-4 flex-col border border-slate-6 dark:border-slatedark-6 rounded-lg p-4"
            >
              <div className="flex gap-2 items-center justify-between">
                <div className="flex gap-2 items-center">
                  <div className="text-xs bg-red-6 dark:bg-reddark-6 text-red-normal rounded-lg px-1.5 py-1 w-fit border border-red-6 dark:border-reddark-6">
                    -{coupon.discount_percentage}%
                  </div>
                  <div className="font-medium text-lg">{coupon.name}</div>
                  <SimpleTooltip message="Copy code">
                    <button
                      className="text-xs bg-slate-action border border-slate-normal rounded-lg px-1.5 py-1"
                      type="button"
                      onClick={() => {
                        void navigator.clipboard.writeText(coupon.code);
                        toast({
                          title: "Code copied to clipboard",
                        });
                      }}
                    >
                      {coupon.code}
                    </button>
                  </SimpleTooltip>
                </div>
                <DeleteCouponDialog
                  coupon={coupon}
                  algorithmId={parseInt(feedId)}
                >
                  <SimpleTooltip message="Delete coupon">
                    <Button size="smIcon" variant="destructive">
                      <span className="sr-only">Delete coupon</span>
                      <TrashIcon />
                    </Button>
                  </SimpleTooltip>
                </DeleteCouponDialog>
              </div>
              {Boolean(
                coupon.max_uses || coupon.expires_at || coupon.uses_count,
              ) && (
                <div className="flex flex-col gap-2 text-sm text-slate-dim">
                  {coupon.max_uses ? (
                    <div className="flex gap-2 items-center">
                      <div>Uses:</div>
                      <div>
                        {coupon.uses_count} / {coupon.max_uses}
                      </div>
                    </div>
                  ) : coupon.uses_count ? (
                    <div className="flex gap-2 items-center">
                      <div>Uses:</div>
                      <div>{coupon.uses_count}</div>
                    </div>
                  ) : null}
                  {coupon.expires_at && (
                    <div className="flex gap-2 items-center">
                      <div>Expires:</div>
                      <div>{format(new Date(coupon.expires_at), "PPP")}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CouponMenu({ feedId }: { feedId: string }) {
  const { data: coupons } = useQuery(getCouponsQueryOptions(parseInt(feedId)));

  return (
    <DropdownMenu>
      <SimpleTooltip message="Coupon options">
        <MenuTrigger asChild>
          <Button size="sm" variant="action">
            <TicketPercentIcon />
            Coupons
          </Button>
        </MenuTrigger>
      </SimpleTooltip>
      <MenuContent align="end">
        <CreateCouponDialog feedId={feedId}>
          <MenuItem onSelect={(e) => e.preventDefault()}>
            <PlusIcon />
            Create
          </MenuItem>
        </CreateCouponDialog>
        <ManageCouponsDialog feedId={feedId}>
          <MenuItem
            onSelect={(e) => e.preventDefault()}
            disabled={!coupons || coupons.length === 0}
          >
            <Edit2 />
            Edit Coupons
          </MenuItem>
        </ManageCouponsDialog>
      </MenuContent>
    </DropdownMenu>
  );
}

function MonetizationSection({ feedId }: { feedId: string }) {
  const { data: campaigns, isPending } = useQuery(
    campaignsForAlgorithmQueryOptions(parseInt(feedId)),
  );

  if (isPending) {
    return <Spinner />;
  }

  const requests =
    campaigns?.filter((c) => c.campaign_status.state === "proposed") || [];
  const hasRequests = requests.length > 0;
  const accepted =
    campaigns?.filter(
      (c) =>
        c.campaign_status.state === "active" ||
        c.campaign_status.state === "accepted",
    ) || [];
  const hasAccepted = accepted.length > 0;

  return (
    <>
      <div className="flex items-center justify-between pb-2 border-b border-slate-7 dark:border-slatedark-7 mt-10">
        <H2 className="border-none pb-0">Monetization</H2>
        <CouponMenu feedId={feedId} />
      </div>
      {hasRequests || hasAccepted ? (
        <>
          {hasRequests && (
            <InboundCampaignTable
              title="Requests"
              tooltipMessage="Campaigns that are requesting to advertise on your feed."
              emptyMessage="No campaigns yet!"
              data={requests}
              variant="approval"
            />
          )}
          {hasAccepted && (
            <InboundCampaignTable
              title="Accepted"
              tooltipMessage="Campaigns that being run on your feeds."
              emptyMessage="No campaigns running!"
              data={accepted}
              variant="active"
            />
          )}
        </>
      ) : (
        <div className="text-sm text-slate-dim">
          No active campaigns or requests.
        </div>
      )}
    </>
  );
}

function RouteComponent() {
  const navigate = useNavigate({ from: "/app/feed/$feedId/view" });
  const { feedId } = Route.useParams();
  const { data: feed, refetch } = useSuspenseQuery(
    feedQueryOptions(parseInt(feedId)),
  );
  const { data: user } = useSuspenseQuery(getCurrentUserQueryOptions());

  // show migrating banner only after 10s since it's pretty fast
  const showMigratingBanner = useDelay(10_000);
  const isMigrating = feed.status === "migrating";

  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    if (!isMigrating) return;

    const interval = setInterval(() => {
      refetchRef.current().catch(console.error);
    }, 1000);

    return () => clearInterval(interval);
  }, [isMigrating]);

  const hideSensitiveContent = !(
    feed.settings?.show_nsfw ??
    user.settings?.show_nsfw ??
    false
  );
  const isSimpleFeed =
    feed.draft_algorithm_manifest.metadata?.snapshot &&
    "type" in feed.draft_algorithm_manifest.metadata.snapshot &&
    feed.draft_algorithm_manifest.metadata.snapshot.type === "simple";

  return (
    <Page>
      <BackfillBanner feedId={parseInt(feedId)} />
      <GymInvitation feedId={parseInt(feedId)} />
      {feed.status === "migration_error" && (
        <Banner variant="destructive">
          <div className="flex gap-4 items-center">
            <CrossCircledIcon className="h-5 w-5" />
            <div>
              There was an error migrating your feed.{" "}
              <a
                href="https://discord.gg/n936Bz7Pug"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold underline"
              >
                Please contact support
              </a>
              .
            </div>
          </div>
        </Banner>
      )}
      {feed.status === "migrated" && (
        <Banner variant="success">
          <div className="flex gap-4 items-center">
            <RocketIcon className="h-5 w-5" />
            <div>
              Your feed has been migrated to Graze! You can now publish it to
              Bluesky.
            </div>
          </div>
        </Banner>
      )}
      {feed.status === "migrating" && showMigratingBanner && (
        <Banner variant="warning">
          <div className="flex gap-4 items-center">
            <ExclamationTriangleIcon className="h-5 w-5" />
            <div>
              This feed is currently migrating the posts that were on your feed
              to our system. This process can take a few minutes. Check back
              later!
            </div>
          </div>
        </Banner>
      )}
      <DepreactedFeedFeaturesBanner feedId={feedId} />
      <ViewFeedHeader feedId={feedId} />
      <FeedPulseSection feedId={feedId} />

      {feed.description && (
        <Blockquote>
          <Description className="text-xs">{feed.description}</Description>
        </Blockquote>
      )}
      <div className="flex flex-col gap-2 md:gap-4 mt-4">
        <FeedSuggestions feed={feed} />
        <H2 className="mb-2">Analytics</H2>
        <FeedAnalyticsChart feedId={feedId} />
        <TopContent />
        <StickyPosts feed={feed} />
        <EngagementTable feedId={feedId} />
        {feed.monetization_available && <MonetizationSection feedId={feedId} />}
        <H2 className="mb-2">Feed Definition</H2>
        <HideSensitiveContentContext value={hideSensitiveContent}>
          {isSimpleFeed ? (
            <div className="flex flex-col gap-6 md:gap-8">
              <SimpleEditor
                feedId={feed.id}
                key={feed.id}
                feedVersion={feed.draft_algorithm_manifest}
              />
            </div>
          ) : (
            <FeedEditor
              defaultMode={feed.settings?.editor_mode}
              key={feed.draft_algorithm_manifest_id}
              feedVersion={feed?.draft_algorithm_manifest}
              feedId={feed.id}
              testPosts={
                feed.draft_algorithm_manifest?.metadata &&
                "testPosts" in feed.draft_algorithm_manifest.metadata
                  ? feed.draft_algorithm_manifest.metadata.testPosts
                  : []
              }
              onEdit={() => {
                navigate({
                  to: "/app/feed/$feedId/edit",
                  params: { feedId: `${feedId}` },
                  search: { fullscreen: true },
                }).catch(console.error);
              }}
            />
          )}
        </HideSensitiveContentContext>
      </div>
    </Page>
  );
}

/**
 * What the feed is doing before it has anything in it.
 *
 * A new feed is seeded from the archives the moment it publishes, and that now
 * finishes in seconds — but until it does, the feed is genuinely empty, and
 * without this the author is looking at a blank page with nothing to tell them
 * whether it is working, broken, or just matching nothing. That was a real
 * report: a feed created and checked a minute later looked broken, while the
 * backfill had in fact filled it 37 seconds in.
 *
 * Only ever shown about a RECENT job. The status record lives for 7 days, and a
 * "done" banner on a week-old feed is noise, not information.
 */
function BackfillBanner({ feedId }: { feedId: number }) {
  const { data, dataUpdatedAt } = useQuery({
    queryKey: ["backfill-status", feedId],
    queryFn: () => getBackfillStatus(feedId),
    // Jobs run in seconds now, so poll fast enough to actually show progress
    // rather than one frame of it. Stops entirely once nothing is running.
    refetchInterval: (query) => (query.state.data?.in_progress ? 2000 : false),
  });

  if (!data || data.status === "none") return null;

  // Recency is measured against when this data was FETCHED, not against a
  // clock read while rendering. `Date.now()` in render is impure — React may
  // re-render at any moment, so the banner could appear or vanish without the
  // data changing, and `react-hooks/purity` rejects it outright. `dataUpdatedAt`
  // comes from the query cache, is stable for a given result, and asks the
  // question we actually mean: was the job recent as of the last time we
  // looked? While a job runs this refreshes every 2s along with the poll.
  const updatedAt = data.updated_at ? Date.parse(data.updated_at) : NaN;
  const isRecent =
    Number.isFinite(updatedAt) && dataUpdatedAt - updatedAt < 10 * 60 * 1000;

  if (data.in_progress) {
    const total = data.archives_total ?? 0;
    const done = data.archives_done ?? 0;
    const progress =
      total > 0 ? ` — ${Math.min(99, Math.round((done / total) * 100))}%` : "";
    // Same rule as the finished banner: a count that is not this feed's own is
    // not this feed's progress.
    const found =
      data.per_feed_counts === true &&
      data.matches_emitted &&
      data.matches_emitted > 0
        ? ` ${data.matches_emitted} posts so far.`
        : "";
    return (
      <Banner variant="warning">
        <div className="flex gap-4 items-center">
          <Loader2Icon className="h-5 w-5 animate-spin" />
          <div>
            Filling this feed with matching posts from the last few hours
            {progress}.{found} This usually takes well under a minute — the page
            updates itself.
          </div>
        </div>
      </Banner>
    );
  }

  if (!isRecent) return null;

  if (data.status === "error" || data.status === "skipped") {
    return (
      <Banner variant="destructive">
        <div className="flex gap-4 items-center">
          <CrossCircledIcon className="h-5 w-5" />
          <div>
            Couldn't fill this feed from recent history
            {data.message ? `: ${data.message}` : "."} Live posts will still
            arrive as they are published.
          </div>
        </div>
      </Banner>
    );
  }

  if (data.status === "done") {
    // `matches_emitted` is only about THIS feed when the worker says so. A
    // refill job replays every feed in its message together, and a worker older
    // than that flag wrote the job-wide total into every participant's record
    // -- feed 11597 read 3,400,257 for a day on which ~4,082 rows reached it.
    // A number that size, attached to a feed that received almost none, is
    // worse than no number: a user reported their refill "lying" about posts it
    // had added, and the number was the only evidence they had.
    if (data.per_feed_counts !== true) {
      return (
        <Banner variant="success">
          <div className="flex gap-4 items-center">
            <RocketIcon className="h-5 w-5" />
            <div>
              Finished filling this feed from recent history. Open the feed to
              see what arrived.
              {data.warning ? ` ${data.warning}.` : ""}
            </div>
          </div>
        </Banner>
      );
    }

    if (data.matches_emitted === 0) {
      return (
        <Banner variant="warning">
          <div className="flex gap-4 items-center">
            <ExclamationTriangleIcon className="h-5 w-5" />
            <div>
              Nothing from the last few hours matched this feed, so it starts
              empty. It will fill as matching posts are published — if that
              doesn't happen, the feed's rules are probably narrower than you
              meant.
              {data.warning ? ` ${data.warning}.` : ""}
            </div>
          </div>
        </Banner>
      );
    }
    return (
      <Banner variant="success">
        <div className="flex gap-4 items-center">
          <RocketIcon className="h-5 w-5" />
          <div>
            Added {data.matches_emitted} posts from recent history.
            {data.warning ? ` ${data.warning}.` : ""}
          </div>
        </div>
      </Banner>
    );
  }

  return null;
}
